import React, {useEffect, useRef, useState} from 'react';
import * as Yup from 'yup';
import {
  Card,
  CardContent,
  FormControl,
  Grid,
  Box,
  List,
  ListItem,
} from '@mui/material';
import {Helmet} from 'react-helmet-async';
import useAuth from '../../hooks/useAuth';
import {useNavigate} from 'react-router-dom';
import {
  Divider,
  StyledErrorMessage,
  StyledTargetedDistributionWrapper,
  StyledUploadFileSection,
  Typography,
} from './TargetedDistributions.style';
import {
  Breadcrumbs,
  Button,
  Chip,
  Input,
  ModalConfirm,
  RadioGroups,
} from '../../components';
import {
  CreditVoucherParams,
  CsvFileInfosType,
  SelectOptionsProps,
  typeRoutes,
} from '../../types/typeTargetedDistributions';
import {
  AddToPocketOptions,
  uploadFileDefault,
  getListTicketAvailable,
  getTicketDetail,
  initialValuesDefault,
  isDisplayUsageTypeAlert,
  MembershipTypeOptions,
  onChooseCSVFile,
  BooleanYesNoOptions,
  quantityColumns,
  quantityRows,
  renderUsageTypeAlert,
  targetedDistributionColumns,
  TargetedDistributionsTable,
  formatBytes,
  VerifyMembershipID,
  MembershipTypeEnums,
  YesNoEnums,
  ContentElement,
  FileSizeLimit,
  CreditVoucherPayload,
  initialTouchedDefault,
} from '../../utils/targeted-distributions/TargetedDistributionsUtils';
import SingleSelectAutocomplete from '../../components/Selects/SingleSelect/SingleSelect';
import {images} from '../../constants/image';
import ModalAddNewVoucher from '../voucher/components/ModalAddNewVoucher/ModalAddNewVoucher';
import ModalInformations from '../../components/Modal/ModalInformations/ModalInformations';
import {TicketVisibilityEnums} from '../../utils/voucher/VoucherUtils';
import {Formik} from 'formik';
import {
  IsNullOrEmptyOrUndefined,
  StringIsNullOrEmptyOrUndefined,
} from '../../utils/helpers';
import VoucherService from '../../services/Voucher/voucher.service';
import success from '../../constants/success';
import {path} from '../../routes/path';
import colors from '../../theme/colors';
import {filter, groupBy, has} from 'lodash';
import {targetedDistributionVoucherLabels} from '../../constants/targetedDistributionVoucher';
import {rbacPermissions} from '../../constants/roleBasedAccessControlConstants';

const TargetedDistributions = () => {
  const {showNotification, checkRbac} = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const timeoutRef = useRef<any>(null);
  const [ticketOptions, setTicketOptions] = useState<SelectOptionsProps[]>([]);
  const [csvFiles, setCsvFiles] = useState<File[]>([]);
  const [ticketDetail, setTicketDetail] = useState<any | null>(null);
  const [isShowNextForm, setIsShowNextForm] = useState(false);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [openModalAddVoucher, setOpenModalAddVoucher] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenMembershipFileInfoModal, setIsOpenMembershipFileInfoModal] =
    useState<boolean>(false);
  const [selectedTicketNumberId, setSelectedTicketNumberId] =
    useState<any>(null);
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState<boolean>(false);
  const [isValidatedMembershipId, setIsValidatedMembershipId] =
    useState<boolean>(false);
  const [isValidatedCSV, setIsValidatedCSV] = useState<boolean>(false);
  const [initialValues, setInitialValues] =
    useState<CreditVoucherParams>(initialValuesDefault);
  const [csvFileInfos, setCSVFileInfos] =
    useState<CsvFileInfosType>(uploadFileDefault);
  const [isValidatedCSVValidation, setIsValidatedCSVValidation] =
    useState<boolean>(false);
  const [validatedCSVValidationErrors, setValidatedCSVValidationErrors] =
    useState<string[]>([]);
  useEffect(() => {
    if (isValidatedCSV === false) {
      setIsValidatedCSVValidation(false);
      setValidatedCSVValidationErrors([]);
    } else {
      const localErrors: string[] = [];
      const membershipIDs = csvFileInfos.membershipIDs;
      if (!Array.isArray(membershipIDs) || membershipIDs.length === 0) {
        localErrors.push('Missing membership IDs');
      } else {
        const cleanString = (str: string) => {
          // Remove all \r and \n characters from the entire string
          // Then trim leading and trailing spaces
          const cleanedInternal = str.replace(/[\r\n]/g, '');
          return cleanedInternal.trim();
        };
        const groupedItems = groupBy(membershipIDs, cleanString);
        const duplicateGroups = filter(groupedItems, group => group.length > 1);
        const isMissingMembershipIDs = has(groupedItems, '');
        if (duplicateGroups.length > 0) {
          localErrors.push('Duplicate membership IDs');
        }
        if (isMissingMembershipIDs) {
          localErrors.push('Missing membership IDs');
        }
      }
      setValidatedCSVValidationErrors(localErrors);
      setIsValidatedCSVValidation(localErrors.length === 0);
    }
  }, [isValidatedCSV, csvFileInfos]);

  const handleGetListTicketAvailable = async () => {
    const ticketRes = await getListTicketAvailable();
    if (ticketRes?.data && Array.isArray(ticketRes.data.items)) {
      const listOptions: SelectOptionsProps[] = ticketRes?.data?.items.map(
        t => {
          return {
            label: `[${t.numberId}] ${t.title} | ${t.excerpt}`,
            value: t.numberId,
            id: t.id,
            targetedDistribution: t.targetedDistribution,
          };
        }
      );
      setTicketOptions(listOptions.sort((a, b) => a.value - b.value));
    }
  };

  const toggleModalAdd = () => {
    setOpenModalAddVoucher(!openModalAddVoucher);
  };

  const toggleOpenInfoModal = () => {
    setIsOpenMembershipFileInfoModal(!isOpenMembershipFileInfoModal);
  };
  const toggleOpenConfirmModal = () => {
    setIsOpenModalConfirm(!isOpenModalConfirm);
  };

  const reloadPage = async () => {
    setSelectedTicketNumberId(null);
    setInitialValues(initialValuesDefault);
    setCSVFileInfos(uploadFileDefault);
    setTicketDetail(null);
  };

  const onUploadCSVFiles = async () => {
    setIsOpenModalConfirm(false);
    try {
      const urls: string[] = [];
      for (const file of csvFiles) {
        const fileName = file.name;
        const fileId = fileName.split('_')[0];
        const filePart = fileName.split('_')[1].replace('.csv', '');
        // Await each file upload to ensure it happens sequentially
        await new VoucherService()
          .uploadTargetedMembersCSV(file, fileId, filePart)
          .then(res => {
            if (res?.success && res?.code === 200) {
              urls.push(res.data);
            }
          });
      }

      return {
        success: true,
        urls: urls,
      };
    } catch (error) {
      // Handle any errors
      setIsSubmitting(false);
      showNotification({
        message: 'Error uploading files. Please try again.',
        variation: 'error',
      });
      return {
        success: false,
        urls: [],
      };
    }
  };

  const onSubmit = async (
    values,
    errors,
    resetValues: any,
    isValidateNoti = false
  ) => {
    const isHaveErrors =
      errors !== null &&
      Object.values(errors).length > 0 &&
      Object.values(errors).some(f => f !== '');
    if (isHaveErrors === true) return false;
    setIsSubmitting(true);
    const payload: CreditVoucherPayload = {
      membershipType: {
        type: 1,
        membershipIds:
          values.membershipType === MembershipTypeEnums.SINGLE
            ? [values.membershipId]
            : [],
        membershipCSVs: [],
        totalCsvRows:
          csvFileInfos.totalNumberOfMembers > 0
            ? csvFileInfos.totalNumberOfMembers
            : null,
      },
      addToPocket:
        ticketDetail?.cost === 0
          ? values.addToPocket === YesNoEnums.YES
            ? true
            : false
          : false,
      ticketId: values.ticketId,
      quantity: values.quantity,
      deductPoints: values.deductPoints === YesNoEnums.YES ? true : false,
      pushNotification:
        values.pushNotification === YesNoEnums.YES ? true : false,
      pushNotificationText: StringIsNullOrEmptyOrUndefined(
        values.pushNotificationText
      )
        ? null
        : values.pushNotificationText,
      pushNotificationTitle: StringIsNullOrEmptyOrUndefined(
        values.pushNotificationTitle
      )
        ? null
        : values.pushNotificationTitle,
      pushNotificationUrl: values.pushNotificationUrl,
      showExclusiveB2CBanner:
        values.showExclusiveB2CBanner === YesNoEnums.YES
          ? values.addToPocket === YesNoEnums.NO
            ? true
            : false
          : false,
      showExclusiveB2CBannerText: StringIsNullOrEmptyOrUndefined(
        values.showExclusiveB2CBannerText
      )
        ? null
        : values.showExclusiveB2CBannerText,
      showExclusiveB2CBannerCta: StringIsNullOrEmptyOrUndefined(
        values.showExclusiveB2CBannerCta
      )
        ? null
        : values.showExclusiveB2CBannerCta,
      showExclusiveB2CBannerUrl: values.showExclusiveB2CBannerUrl,
      dryRun: false,
      syncToSiah: true,
      overrideVoucherType: false,
      overrideQuantity: true,
      visibility:
        ticketDetail.targetedDistribution === TicketVisibilityEnums.PUBLIC
          ? false
          : true,
    };
    const isUnNotification =
      isValidateNoti &&
      values.addToPocket === YesNoEnums.NO &&
      values.pushNotification === YesNoEnums.NO &&
      values.showExclusiveB2CBanner === YesNoEnums.NO;
    if (isUnNotification) {
      setInitialValues(values);
      setIsSubmitting(false);
      toggleOpenConfirmModal();
      return false;
    }
    if (values.membershipType === MembershipTypeEnums.MULTIPLE) {
      const result = await onUploadCSVFiles();
      if (!result?.success) return false;
      payload.membershipType.membershipCSVs = result.urls;
    }

    await new VoucherService()
      .creditVoucher(payload)
      .then(async res => {
        if (res?.success === true && res?.code === 200) {
          resetValues();
          reloadPage();
          setIsSubmitting(false);
          return navigate(
            `/${path.VOUCHER}/${path.DISTRIBUTION_LOGS}?_page=0&_size=10&_sort=updatedDateTime:desc&status_eq=1`
          );
        } else {
          setIsSubmitting(false);
          showNotification({
            message: res.errorMessage,
            variation: 'error',
          });
        }
      })
      .catch(e => {
        setIsSubmitting(false);
        showNotification({
          message: e.errorMessage,
          variation: 'error',
        });
      });

    return false;
  };

  const handleClickButtonUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const removeCurrentCSVFile = async (
    setFieldValue: (e: string, value: any) => void
  ) => {
    if (fileInputRef?.current) fileInputRef.current.value = '';
    setCSVFileInfos(uploadFileDefault);
    setFieldValue('uploadFile', '');
    //await onChange('uploadFile', uploadFileDefault);
    setIsShowNextForm(false);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    values: CreditVoucherParams,
    setFieldValue: (key: string, value: any) => void
  ) => {
    setFieldValue('uploadFile', '');

    let initValues = {...values};
    const res = await onChooseCSVFile(event, setCsvFiles);
    if (res.totalNumberOfMembers > 0) {
      setFieldValue('membershipIds', res.membershipIDs);
      initValues = {...initValues, membershipIds: res.membershipIDs};
    } else {
      setFieldValue('membershipIds', []);
      initValues = {...initValues, membershipIds: []};
    }
    const file = event.target.files ? event.target.files[0] : '';
    if (file) {
      initValues = {...initValues, uploadFile: file};
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (res.size <= FileSizeLimit) {
      setCSVFileInfos(res);
    } else {
      setCSVFileInfos(uploadFileDefault);
    }
    setIsShowNextForm(initValues.membershipIds.length > 0);
    return file;
  };

  const handleGetTicketDetail = async (id: string) => {
    if (id !== '') {
      const ticketDetail = await getTicketDetail(id);
      return ticketDetail;
    }
  };

  useEffect(() => {
    handleGetListTicketAvailable();
  }, []);

  const renderTargetedDistributionChip = (
    value: any,
    isRenderingOption = false
  ) => {
    if (!value) {
      return undefined;
    }
    const isTargetedDistribution = value?.targetedDistribution;
    const chipConfig = {
      sx: {
        py: 0,
        px: 1,
        backgroundColor: isTargetedDistribution
          ? colors.purple[100]
          : colors.orange[100],
        color: colors.coolGrey[100],
        '& .MuiChip-label': {
          fontSize: 13,
          fontWeight: 'bold',
          lineHeight: 1.5,
        },
        ...(isRenderingOption
          ? {
              mr: 1,
            }
          : {}),
      },
    };
    return (
      <Chip
        {...chipConfig}
        label={
          isTargetedDistribution
            ? targetedDistributionVoucherLabels.TARGETED
            : targetedDistributionVoucherLabels.PUBLIC
        }
      />
    );
  };

  return (
    <StyledTargetedDistributionWrapper>
      {/* Title*/}
      <Helmet title="Targeted Distributions" />

      <Grid justifyContent="space-between" container spacing={2} columns={16}>
        <Grid item xs={8}>
          <Typography variant="h3" gutterBottom>
            Targeted distribution of voucher
          </Typography>

          <Breadcrumbs
            routes={[
              typeRoutes('Voucher', undefined, true),
              typeRoutes('Targeted distribution of voucher', undefined, true),
            ]}
          />
        </Grid>
      </Grid>

      {/* Modals */}
      <ModalAddNewVoucher
        visible={openModalAddVoucher}
        reloadPage={reloadPage}
        onClose={toggleModalAdd}
      />

      <ModalInformations
        visible={isOpenMembershipFileInfoModal}
        icon={images.icon_cicle_warning}
        onClose={toggleOpenInfoModal}
        title="CSV file label"
        description='Please label the column for the Islander Member ID as "MembershipID".'
        descColor="#003349"
        renderContentElement={ContentElement}
      />

      <Divider my={6} />

      {/* Content */}
      <Formik
        initialValues={initialValues}
        initialTouched={initialTouchedDefault}
        enableReinitialize
        validateOnChange={false} // Disable automatic validation on change
        validateOnBlur={false} // Disable automatic validation on blur
        validationSchema={Yup.object().shape({
          membershipType: Yup.number(),
          membershipId: Yup.string().when('membershipType', {
            is: 1,
            then: Yup.string()
              .required('Membership ID is required')
              .test(
                'verify-membership-ids',
                'Membership ID is not valid. Try again.',
                async id => {
                  if (!id) return true; // Skip if no IDs
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (emailRegex.test(id)) return false;
                  const isExisted = await VerifyMembershipID(id);
                  return isExisted; // Call API and validate
                }
              ),
            otherwise: Yup.string(),
          }),
          uploadFile: Yup.mixed().when('membershipType', {
            is: 2,
            then: Yup.mixed()
              .test('fileSize', 'File is above 250MB', async file => {
                if (!file || !(file instanceof Blob)) {
                  return true;
                }
                const ValidateSizeLimit = async () => {
                  return !file || (file && file.size <= FileSizeLimit);
                };
                // Check if file exists and its size is below the limit
                return await ValidateSizeLimit();
              })
              .test('fileType', 'File must be a CSV', async file => {
                if (!file || !(file instanceof Blob)) {
                  return true;
                }
                const ValidateType = async () => {
                  return !file || (file && file.type === 'text/csv');
                };
                // Ensure the file type is CSV
                return await ValidateType();
              })
              .test(
                'containsMembershipID',
                'CSV must contain MembershipID column',
                async file => {
                  if (!file || !(file instanceof Blob)) {
                    return true;
                  }
                  // Read the file content asynchronously
                  const text = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsText(file);
                  });

                  // Split the file into rows and check for MembershipID column in header
                  const rows = text.split('\n');
                  if (rows.length > 0) {
                    const cleanedRow0 = rows[0].replace(/[\r\n]+/g, '').trim();
                    const header = cleanedRow0.split(
                      cleanedRow0.includes(',') ? ',' : ';'
                    ); // First row as header
                    if (header.includes('MembershipID')) {
                      return true; // MembershipID found in the header
                    }
                  }

                  return false; // MembershipID column not found
                }
              )
              .required('CSV file is required'),
            otherwise: Yup.number(),
          }),
          quantity: Yup.number()
            .nullable()
            .required('The quantity is required!')
            .min(1, 'Quantity should be greater than 0'),
          pushNotificationTitle: Yup.string().when(
            ['membershipId', 'uploadFile', 'quantity', 'pushNotification'],
            {
              is: (
                membershipId: string,
                uploadFile: any,
                quantity: number,
                pushNotification: number
              ) =>
                (membershipId !== '' || uploadFile !== '') &&
                quantity > 0 &&
                pushNotification === YesNoEnums.YES,
              then: Yup.string()
                .required('Push notification title is required')
                .max(178, 'Message must be less than 178 characters'),
              otherwise: Yup.string(),
            }
          ),
          pushNotificationText: Yup.string().when(
            ['membershipId', 'uploadFile', 'quantity', 'pushNotification'],
            {
              is: (
                membershipId: string,
                uploadFile: any,
                quantity: number,
                pushNotification: number
              ) =>
                (membershipId !== '' || uploadFile !== '') &&
                quantity > 0 &&
                pushNotification === YesNoEnums.YES,
              then: Yup.string()
                .required('Push notification text is required')
                .max(240, 'Message must be less than 240 characters'),
              otherwise: Yup.string(),
            }
          ),
          showExclusiveB2CBannerText: Yup.string().when(
            [
              'membershipId',
              'membershipIds',
              'quantity',
              'showExclusiveB2CBanner',
              'addToPocket',
            ],
            {
              is: (
                membershipId: string,
                membershipIds: string[],
                quantity: number,
                showExclusiveB2CBanner: number,
                addToPocket: number
              ) =>
                (membershipId !== '' || membershipIds.length > 0) &&
                quantity > 0 &&
                showExclusiveB2CBanner === YesNoEnums.YES &&
                addToPocket === YesNoEnums.NO,
              then: Yup.string()
                .required('Show exclusive B2C banner text is required')
                .max(60, 'Message must be less than 60 characters'),
              otherwise: Yup.string(),
            }
          ),
          showExclusiveB2CBannerCta: Yup.string().when(
            [
              'membershipId',
              'uploadFile',
              'quantity',
              'showExclusiveB2CBanner',
              'addToPocket',
            ],
            {
              is: (
                membershipId: string,
                uploadFile: any,
                quantity: number,
                showExclusiveB2CBanner: number,
                addToPocket: number
              ) =>
                (membershipId !== '' || uploadFile !== '') &&
                quantity > 0 &&
                showExclusiveB2CBanner === YesNoEnums.YES &&
                addToPocket === YesNoEnums.NO,
              then: Yup.string()
                .required('Show exclusive B2C banner CTA is required')
                .max(20, 'Message must be less than 20 characters'),
              otherwise: Yup.string(),
            }
          ),
        })}
      >
        {({
          errors,
          setFieldValue,
          setFieldError,
          touched,
          values,
          validateField,
          setFieldTouched,
          setValues,
          validateForm,
        }) => {
          const isShowMore =
            ((isValidatedCSV && isValidatedCSVValidation) ||
              isValidatedMembershipId) &&
            isShowNextForm &&
            !isUploadInProgress &&
            IsNullOrEmptyOrUndefined(errors.membershipId) &&
            IsNullOrEmptyOrUndefined(errors.uploadFile);

          const isDisableSubmitBtn =
            (values.membershipId === '' && values.uploadFile === '') ||
            (values.pushNotification === YesNoEnums.YES &&
              (values.pushNotificationText === '' ||
                values.pushNotificationTitle === '')) ||
            (values.showExclusiveB2CBanner === YesNoEnums.YES &&
              values.addToPocket === YesNoEnums.NO &&
              (values.showExclusiveB2CBannerText === '' ||
                values.showExclusiveB2CBannerCta === '')) ||
            Object.values(errors).some(f => !IsNullOrEmptyOrUndefined(f));

          const setTouchedFields = async (fields: string[]) => {
            fields.forEach(f => {
              setFieldTouched(f, true);
            });
            return fields;
          };
          const resetValues = async () => {
            setValues(initialValuesDefault);
          };

          const isShowQuantityRow = () => {
            if (values.membershipType === MembershipTypeEnums.SINGLE) {
              return (
                values.membershipId !== '' &&
                isValidatedMembershipId &&
                StringIsNullOrEmptyOrUndefined(errors.membershipId)
              );
            } else if (values.membershipType === MembershipTypeEnums.MULTIPLE) {
              return (
                values.uploadFile !== '' &&
                isValidatedCSV &&
                isValidatedCSVValidation &&
                IsNullOrEmptyOrUndefined(errors.uploadFile) &&
                !isUploadInProgress
              );
            }
          };

          const ValidateFieldMembership = async () => {
            if (values.membershipType === MembershipTypeEnums.SINGLE) {
              validateField('membershipId');
            }
            if (values.membershipType === MembershipTypeEnums.MULTIPLE) {
              validateField('uploadFile');
            }

            return true;
          };

          const handleChangeTicketId = async (value: any) => {
            handleResetAllFields();
            setIsValidatedCSV(false);
            setIsValidatedMembershipId(false);
            setSelectedTicketNumberId(value);
            setFieldValue('ticketId', value?.id ?? null);
            const ticketDetails = await handleGetTicketDetail(value?.id ?? '');
            setFieldValue(
              'pushNotificationUrl',
              ticketDetails?.ticketUrl ?? ''
            );
            setFieldValue(
              'showExclusiveB2CBannerUrl',
              ticketDetails?.ticketUrl ?? ''
            );
            const addToPocketValue =
              ticketDetails?.cost > 0 ? YesNoEnums.NO : YesNoEnums.YES;
            setFieldValue('addToPocket', addToPocketValue);
            setFieldValue(
              'quantity',
              addToPocketValue === YesNoEnums.NO ? 1 : null
            );
            setFieldError('membershipId', '');
            setFieldError('uploadFile', '');
            setCSVFileInfos(uploadFileDefault);
            setTicketDetail(ticketDetails);
            setIsShowNextForm(false);
          };

          const handleChangeUploadFile = async e => {
            setIsValidatedCSV(false);
            setIsUploadInProgress(true);
            setFieldError('uploadFile', '');

            const fileRes = await handleFileChange(e, values, setFieldValue);
            setFieldValue('uploadFile', fileRes);
            setTimeout(async () => {
              const fetch = async () => {
                const isSuccess = await ValidateFieldMembership();

                if (isSuccess) {
                  setTimeout(async () => {
                    setIsUploadInProgress(false);
                    return setIsValidatedCSV(true);
                  }, 200);
                }
              };
              await fetch();

              return;
            }, 1000);
          };

          const handleChangeMembershipType = (value: number) => {
            setIsValidatedCSV(false);
            setIsValidatedMembershipId(false);
            setFieldValue('membershipType', value);
            setFieldValue('membershipId', '');
            setFieldValue('membershipIds', []);
            setFieldValue('uploadFile', '');
            if (values.addToPocket === YesNoEnums.YES) {
              setFieldValue('quantity', null);
            }
            setFieldError('membershipId', '');
            setFieldError('uploadFile', '');
            setIsShowNextForm(false);
            setCSVFileInfos(uploadFileDefault);
          };

          const handleChangeQuantity = value => {
            setFieldValue('quantity', value);

            setTimeout(async () => {
              return validateField('quantity');
            }, 200);
          };

          const handleChangeMembershipID = (value: string) => {
            setFieldValue('membershipId', value);
          };

          const handleResetAllFields = () => {
            setValues(initialValuesDefault);
          };

          const handleChangeAddToPocket = (e: any) => {
            const addToPocketValue = Number(e.target.value);
            setFieldValue('addToPocket', addToPocketValue);
            setFieldValue(
              'quantity',
              addToPocketValue === YesNoEnums.NO ? 1 : null
            );
            setFieldError('quantity', '');
            handleChangeShowExclusiveBanner(
              Number(e.target.value) === YesNoEnums.YES
                ? YesNoEnums.NO
                : YesNoEnums.YES
            );
          };

          const handleChangeShowExclusiveBanner = (value: number) => {
            setFieldValue('showExclusiveB2CBanner', value);
            setFieldValue('showExclusiveB2CBannerText', '');
            setFieldValue('showExclusiveB2CBannerCta', '');
            setFieldError('showExclusiveB2CBannerText', '');
            setFieldError('showExclusiveB2CBannerCta', '');
          };

          const handleChangeShowExclusiveBannerText = (e: any) => {
            setFieldValue('showExclusiveB2CBannerText', e.target.value);

            setTimeout(() => {
              validateField('showExclusiveB2CBannerText');
            }, 200);
          };

          const handleChangeShowExclusiveBannerCta = (e: any) => {
            setFieldValue('showExclusiveB2CBannerCta', e.target.value);

            setTimeout(() => {
              validateField('showExclusiveB2CBannerCta');
            }, 200);
          };

          const handleChangePushNotification = (value: number) => {
            setFieldValue('pushNotification', value);
            setFieldValue('pushNotificationTitle', '');
            setFieldValue('pushNotificationText', '');
            setFieldError('pushNotificationTitle', '');
            setFieldError('pushNotificationText', '');
          };

          const handleChangePushNotificationTitle = e => {
            setFieldValue('pushNotificationTitle', e.target.value);
            setTimeout(() => {
              validateField('pushNotificationTitle');
            }, 200);
          };

          const handleChangePushNotificationText = e => {
            setFieldValue('pushNotificationText', e.target.value);
            setTimeout(() => {
              validateField('pushNotificationText');
            }, 200);
          };

          return (
            <form noValidate>
              <Grid container mb="30px">
                <ModalConfirm
                  visible={isOpenModalConfirm}
                  icon={images.icon_cicle_warning}
                  onClose={toggleOpenConfirmModal}
                  onConfirm={async () => {
                    const resErrors = await validateForm(values);
                    onSubmit(values, resErrors, resetValues, false);
                    toggleOpenConfirmModal();
                  }}
                  agreeLabel="Yes"
                  disAgreeLabel="Cancel"
                  maxWidth="sm"
                  reverseBtn={true}
                  title={
                    <Typography variant="h4" fontWeight="bold">
                      Distribute without notifying members?
                    </Typography>
                  }
                  subTitle='Members may not be aware of this voucher as you have selected "No" for all notifications.'
                />
                <Grid item md={12} lg={12}>
                  <Card sx={{padding: '36px 36px 0px 36px'}}>
                    <CardContent>
                      <FormControl fullWidth margin="normal">
                        <Typography variant="body2" mb={2} fontWeight="bold">
                          Select the voucher you would like to distribute{' '}
                        </Typography>
                        <SingleSelectAutocomplete
                          name="ticketId"
                          placeholder="Search for a keyword or browse all vouchers arranged by voucher number"
                          options={ticketOptions}
                          value={selectedTicketNumberId}
                          onChange={async (e, newValue) =>
                            await handleChangeTicketId(newValue)
                          }
                          renderValueLabelStartAdorment={
                            renderTargetedDistributionChip
                          }
                        />
                      </FormControl>

                      {values.ticketId && ticketDetail !== null ? (
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              mb={2}
                              fontWeight="bold"
                              mt={4}
                            >
                              Voucher details{' '}
                            </Typography>
                            <TargetedDistributionsTable
                              columns={targetedDistributionColumns}
                              rows={ticketDetail}
                            />
                            {isDisplayUsageTypeAlert(
                              ticketDetail?.usageType ?? null,
                              ticketDetail?.cost ?? null
                            ) && (
                              <>
                                <Typography
                                  variant="body2"
                                  display="inline-block"
                                  color="#FFFFFF"
                                  fontSize={11}
                                  lineHeight={1}
                                  padding="4.5px 7px"
                                  mr={2}
                                  borderRadius="50%"
                                  sx={{
                                    backgroundColor: '#d32f2f',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  x
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="error"
                                  display="inline-block"
                                  fontWeight="bold"
                                  maxWidth={140}
                                  sx={{
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {renderUsageTypeAlert(
                                    ticketDetail?.usageType ?? null,
                                    ticketDetail?.cost ?? null
                                  )}
                                </Typography>
                              </>
                            )}
                          </Grid>
                          <Divider my={6} sx={{width: '100%'}} />
                          <Grid item>
                            <Typography
                              variant="body2"
                              py={1}
                              fontWeight="bold"
                            >
                              Distribute directly into member’s pocket
                            </Typography>
                            {ticketDetail?.cost === 0 ? (
                              <FormControl fullWidth>
                                <RadioGroups
                                  name="addToPocket"
                                  options={AddToPocketOptions}
                                  values={values.addToPocket}
                                  onChange={handleChangeAddToPocket}
                                />
                              </FormControl>
                            ) : (
                              <Typography variant="body2">
                                Only free vouchers can be directly distributed
                                into member’s pocket
                              </Typography>
                            )}
                          </Grid>
                          <Divider my={6} sx={{width: '100%'}} />
                          <Grid item xs={12}>
                            <Typography
                              variant="body2"
                              mb={2}
                              fontWeight="bold"
                            >
                              Islander member(s) who will receive this voucher{' '}
                            </Typography>
                            <Box>
                              <FormControl fullWidth margin="normal">
                                <RadioGroups
                                  name="membershipType"
                                  options={MembershipTypeOptions}
                                  values={values.membershipType}
                                  onChange={e =>
                                    handleChangeMembershipType(
                                      Number(e.target.value)
                                    )
                                  }
                                />
                              </FormControl>
                            </Box>
                          </Grid>
                          {/* Render form to input membershipId */}
                          {ticketDetail !== null && (
                            <>
                              {values.membershipType ===
                                MembershipTypeEnums.SINGLE && (
                                <Grid item xs={6} marginRight={4}>
                                  <Typography
                                    variant="body2"
                                    mb={2}
                                    fontWeight="bold"
                                  >
                                    Membership ID{' '}
                                    <span className="mandatory-label">(*)</span>
                                  </Typography>
                                  <Box>
                                    <FormControl fullWidth margin="dense">
                                      <Input
                                        className="helperText__unsetMarginLeft"
                                        placeholder="e.g. AA1234567"
                                        size="small"
                                        name="membershipId"
                                        value={values.membershipId}
                                        variant="outlined"
                                        onChange={e => {
                                          if (timeoutRef.current) {
                                            clearTimeout(timeoutRef.current);
                                          }

                                          handleChangeMembershipID(
                                            e.target.value
                                          );
                                          setIsShowNextForm(
                                            e.target.value !== ''
                                          );

                                          timeoutRef.current = setTimeout(
                                            () => {
                                              ValidateFieldMembership();
                                              setTimeout(() => {
                                                setIsValidatedMembershipId(
                                                  true
                                                );
                                              }, 200);
                                            },
                                            800
                                          );
                                        }}
                                        helperText={errors.membershipId}
                                        error={Boolean(errors.membershipId)}
                                      />
                                    </FormControl>
                                  </Box>
                                </Grid>
                              )}
                              {values.membershipType ===
                                MembershipTypeEnums.MULTIPLE && (
                                <>
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      Upload CSV list (Max 250MB){' '}
                                      <span className="mandatory-label">
                                        (*)
                                      </span>
                                      <img
                                        onClick={() => {
                                          toggleOpenInfoModal();
                                        }}
                                        src={images.icon_info}
                                        style={{
                                          display: 'inline-block',
                                          verticalAlign: 'top',
                                          marginLeft: '6px',
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </Typography>
                                    <input
                                      name="uploadFile"
                                      type="file"
                                      accept=".csv"
                                      onChange={handleChangeUploadFile}
                                      style={{display: 'none'}}
                                      ref={fileInputRef}
                                    />
                                    {(values.uploadFile === '' ||
                                      !IsNullOrEmptyOrUndefined(
                                        errors.uploadFile
                                      ) ||
                                      isUploadInProgress) && (
                                      <Button
                                        type="button"
                                        margin="16px 0px 0px 0px"
                                        variant="outlined"
                                        size="small"
                                        loading={isUploadInProgress}
                                        disabled={isUploadInProgress}
                                        onClick={handleClickButtonUploadFile}
                                      >
                                        Upload CSV{' '}
                                      </Button>
                                    )}
                                  </Grid>
                                  {!isUploadInProgress && isValidatedCSV && (
                                    <Grid
                                      item
                                      xs={4}
                                      mt={
                                        IsNullOrEmptyOrUndefined(
                                          errors.uploadFile
                                        )
                                          ? 4
                                          : 0
                                      }
                                    >
                                      {csvFileInfos.nameFile !== '' &&
                                        IsNullOrEmptyOrUndefined(
                                          errors.uploadFile
                                        ) && (
                                          <StyledUploadFileSection item xs={12}>
                                            <Box>
                                              <img
                                                src={images.icon_rectangle_miss}
                                              />
                                            </Box>
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                fontWeight="bolder"
                                                fontSize="13px"
                                              >
                                                {csvFileInfos.nameFile}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                fontSize="11px"
                                              >
                                                {formatBytes(csvFileInfos.size)}
                                              </Typography>
                                            </Box>
                                            <Box>
                                              <img
                                                src={images.icon_trash}
                                                onClick={() =>
                                                  removeCurrentCSVFile(
                                                    setFieldValue
                                                  )
                                                }
                                              />
                                            </Box>
                                          </StyledUploadFileSection>
                                        )}
                                      {Boolean(errors.uploadFile) && (
                                        <StyledErrorMessage>
                                          {errors.uploadFile}
                                        </StyledErrorMessage>
                                      )}
                                    </Grid>
                                  )}
                                  {!isUploadInProgress &&
                                    isValidatedCSV &&
                                    csvFileInfos.nameFile !== '' &&
                                    IsNullOrEmptyOrUndefined(
                                      errors.uploadFile
                                    ) && (
                                      <Grid
                                        item
                                        xs={7}
                                        mt={4}
                                        alignItems="start"
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="center"
                                        mb={4}
                                        ml={4}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                          mb={2}
                                          fontSize={11}
                                        >
                                          Total number of members
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontSize={20}
                                          fontWeight="300"
                                        >
                                          {csvFileInfos.totalNumberOfMembers}
                                        </Typography>
                                        {!isValidatedCSVValidation && (
                                          <Typography
                                            variant="body2"
                                            fontWeight="400"
                                            color="error"
                                            mt={2}
                                          >
                                            The following error(s) were detected
                                            in the CSV file:
                                            <List
                                              sx={{
                                                listStyleType: 'disc',
                                                pl: 4,
                                                '& .MuiListItem-root:first-of-type':
                                                  {
                                                    pt: 0,
                                                  },
                                              }}
                                            >
                                              {validatedCSVValidationErrors.map(
                                                error => (
                                                  <ListItem
                                                    sx={{
                                                      display: 'list-item',
                                                      p: 0,
                                                      pt: 1,
                                                    }}
                                                  >
                                                    {error}
                                                  </ListItem>
                                                )
                                              )}
                                            </List>
                                          </Typography>
                                        )}
                                      </Grid>
                                    )}
                                </>
                              )}
                            </>
                          )}
                          {isShowQuantityRow() && (
                            <Grid container mt={4}>
                              <Grid item xs={2}>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  mt={1}
                                  fontWeight="bold"
                                >
                                  Quantity per member{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Box>
                                  <FormControl margin="none">
                                    <Input
                                      className="helperText__unsetMarginLeft short-field"
                                      placeholder="e.g. 3"
                                      type="number"
                                      size="small"
                                      name="quantity"
                                      readOnly={
                                        values?.addToPocket === YesNoEnums.NO
                                      }
                                      value={values.quantity}
                                      variant="outlined"
                                      helperText={errors.quantity}
                                      onChange={e =>
                                        handleChangeQuantity(
                                          Number(e.target.value)
                                        )
                                      }
                                      error={Boolean(errors.quantity)}
                                    />
                                  </FormControl>
                                  {values?.addToPocket === YesNoEnums.NO && (
                                    <Typography fontSize={11} variant="body2">
                                      To adjust quantity per member, voucher
                                      must be distributed directly into member's
                                      pocket
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={8}>
                                <TargetedDistributionsTable
                                  {...{isFullWidth: false}}
                                  columns={quantityColumns}
                                  rows={quantityRows(
                                    ticketDetail?.quantity ?? null,
                                    ticketDetail?.redemptionCount ?? null,
                                    values?.quantity ?? null,
                                    csvFileInfos?.totalNumberOfMembers ?? 0,
                                    values.addToPocket
                                  )}
                                />
                              </Grid>
                            </Grid>
                          )}
                          {isShowMore &&
                          values.addToPocket === YesNoEnums.NO ? (
                            <>
                              <Divider my={6} sx={{width: '100%'}} />
                              <Grid item xs={12} mb={2}>
                                <FormControl fullWidth>
                                  <Typography
                                    variant="body2"
                                    py={1}
                                    fontWeight="bold"
                                  >
                                    Send exclusive banner to members on B2C
                                    platforms
                                  </Typography>
                                  <RadioGroups
                                    name="showExclusiveB2CBanner"
                                    options={BooleanYesNoOptions}
                                    values={values.showExclusiveB2CBanner}
                                    onChange={e => {
                                      handleChangeShowExclusiveBanner(
                                        Number(e.target.value)
                                      );
                                    }}
                                  />
                                </FormControl>
                              </Grid>
                            </>
                          ) : (
                            ''
                          )}
                          {/* {renderShowExclusiveBanner} */}
                          {isShowMore &&
                          values.showExclusiveB2CBanner &&
                          values.addToPocket === YesNoEnums.NO ? (
                            <>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={1}
                                  fontWeight="bold"
                                >
                                  Description{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  mb={2}
                                  fontSize={11}
                                >
                                  Maximum character count: 60
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      placeholder="e.g. We have an exclusive voucher for you"
                                      size="small"
                                      name="showExclusiveB2CBannerText"
                                      value={values.showExclusiveB2CBannerText}
                                      variant="outlined"
                                      helperText={
                                        errors.showExclusiveB2CBannerText
                                      }
                                      onChange={
                                        handleChangeShowExclusiveBannerText
                                      }
                                      error={Boolean(
                                        errors.showExclusiveB2CBannerText
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={1}
                                  fontWeight="bold"
                                >
                                  Call to Action Copy{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  mb={2}
                                  fontSize={11}
                                >
                                  Maximum character count: 20
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      placeholder="e.g. Redeem now"
                                      size="small"
                                      name="showExclusiveB2CBannerCta"
                                      value={values.showExclusiveB2CBannerCta}
                                      variant="outlined"
                                      helperText={
                                        errors.showExclusiveB2CBannerCta
                                      }
                                      onChange={
                                        handleChangeShowExclusiveBannerCta
                                      }
                                      error={Boolean(
                                        errors.showExclusiveB2CBannerCta
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Link URL
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      size="small"
                                      readOnly
                                      value={values.showExclusiveB2CBannerUrl}
                                      defaultValue=""
                                      variant="outlined"
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                            </>
                          ) : (
                            ''
                          )}
                          {isShowMore && (
                            <Divider my={6} sx={{width: '100%'}} />
                          )}
                          {isShowMore && (
                            <Grid item xs={12} mb={2}>
                              <FormControl fullWidth>
                                <Typography
                                  variant="body2"
                                  py={1}
                                  fontWeight="bold"
                                >
                                  Send push notification to members who have
                                  opted in once voucher has been distributed
                                </Typography>
                                <RadioGroups
                                  name="pushNotification"
                                  options={BooleanYesNoOptions}
                                  values={values.pushNotification}
                                  onChange={e => {
                                    handleChangePushNotification(
                                      Number(e.target.value)
                                    );
                                  }}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {/* {renderPushNoticationForm} */}
                          {isShowMore && values.pushNotification ? (
                            <>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={1}
                                  fontWeight="bold"
                                >
                                  Push notification title{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  mb={2}
                                  fontSize={11}
                                >
                                  Maximum character count: 178
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      placeholder="e.g. Sentosa Islander"
                                      size="small"
                                      name="pushNotificationTitle"
                                      value={values.pushNotificationTitle}
                                      variant="outlined"
                                      helperText={errors.pushNotificationTitle}
                                      onChange={
                                        handleChangePushNotificationTitle
                                      }
                                      error={Boolean(
                                        errors.pushNotificationTitle
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={1}
                                  fontWeight="bold"
                                >
                                  Push notification description{' '}
                                  <span className="mandatory-label">(*)</span>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  mb={2}
                                  fontSize={11}
                                >
                                  Maximum character count: 240
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      placeholder="e.g. Voucher(s) on the house! Use them before they expire."
                                      size="small"
                                      name="pushNotificationText"
                                      value={values.pushNotificationText}
                                      variant="outlined"
                                      helperText={errors.pushNotificationText}
                                      onBlur={() => {}}
                                      onChange={
                                        handleChangePushNotificationText
                                      }
                                      error={Boolean(
                                        errors.pushNotificationText
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  mb={2}
                                  fontWeight="bold"
                                >
                                  Push notification URL
                                </Typography>
                                <Box>
                                  <FormControl fullWidth margin="normal">
                                    <Input
                                      className="helperText__unsetMarginLeft"
                                      size="small"
                                      name="pushNotificationUrl"
                                      readOnly
                                      value={values.pushNotificationUrl}
                                      defaultValue=""
                                      variant="outlined"
                                    />
                                  </FormControl>
                                </Box>
                              </Grid>
                            </>
                          ) : (
                            ''
                          )}
                        </Grid>
                      ) : (
                        checkRbac(
                          {
                            permissionString:
                              rbacPermissions.VOUCHERS__LISTING__CREATE,
                          },
                          <Typography
                            onClick={toggleModalAdd}
                            className="btn__create--voucher"
                            variant="body2"
                          >
                            <img
                              src={images.icon_create_voucher}
                              style={{
                                marginRight: '8px',
                              }}
                              height="17px"
                            />
                            Create new voucher
                          </Typography>
                        )
                      )}
                      <Grid container justifyContent="end">
                        <FormControl margin="normal">
                          <Button
                            className="btn__submit--form"
                            type="button"
                            onClick={async () => {
                              await setTouchedFields(Object.keys(values));
                              const resErrors = await validateForm(values);
                              onSubmit(values, resErrors, resetValues, true);
                            }}
                            disabled={isDisableSubmitBtn}
                            loading={isSubmitting}
                          >
                            Distribute
                          </Button>
                        </FormControl>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </StyledTargetedDistributionWrapper>
  );
};

export default TargetedDistributions;
